// packages/web/src/components/Common/LocationSearchSelect.tsx
import React, { useState, useEffect, useId } from 'react';
import {
  FormControl,
  TextField,
  Autocomplete,
  Box,
  Typography,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import { AlgoliaSearchService } from '@matchpoint/shared';
import { initializeAlgoliaForWeb } from '../../utils/algoliaInit';

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    fontSize: '0.9em',
  },
  '& .MuiInputBase-root': {
    fontSize: '0.9em',
  },
}));

export interface LocationOption {
  label: string;
  value: string;
  type: 'city' | 'country';
  count?: number;
  country?: string; // For cities, include country
  state?: string; // For cities, include state if available
}

export interface LocationSearchSelectProps {
  value: string;
  onChange: (value: string, option?: LocationOption) => void;
  label?: string;
  placeholder?: string;
  type: 'city' | 'country' | 'both';
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  error?: boolean;
  includeAnyOption?: boolean;
  anyOptionLabel?: string;
}

export const LocationSearchSelect: React.FC<LocationSearchSelectProps> = ({
  value = '',
  onChange,
  label = 'Location',
  placeholder = 'Search location...',
  type = 'both',
  size = 'medium',
  fullWidth = true,
  disabled = false,
  required = false,
  helperText,
  error = false,
  includeAnyOption = false,
  anyOptionLabel = 'Any Location',
}) => {
  const [options, setOptions] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  
  // Generate unique ID for accessibility
  const selectId = useId();

  // Find current selected option or create a temporary one
  const selectedOption = options.find(opt => opt.value === value) || 
    (value ? { label: value, value: value, type: 'country' as const } : null);

  // Search for locations using Algolia
  const searchLocations = async (query: string) => {
    if (!query || query.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    
    try {
      await initializeAlgoliaForWeb();
      const algoliaService = AlgoliaSearchService.getInstance();
      
      const promises: Promise<any>[] = [];
      
      // Search cities if needed
      if (type === 'city' || type === 'both') {
        promises.push(algoliaService.searchIndex('courts_city', {
          query,
          page: 0,
          hitsPerPage: 10,
          attributesToRetrieve: ['name', 'country'],
          attributesToHighlight: ['name'],
        }));
      }
      
      // Search countries if needed
      if (type === 'country' || type === 'both') {
        promises.push(algoliaService.searchIndex('courts_country', {
          query,
          page: 0,
          hitsPerPage: 10,
          attributesToRetrieve: ['name'],
          attributesToHighlight: ['name'],
        }));
      }
      
      const results = await Promise.all(promises);
      const locationOptions: LocationOption[] = [];
      
      // Process city results
      if (type === 'city' || type === 'both') {
        const cityResults = results[0];
        if (cityResults?.hits) {
          cityResults.hits.forEach((hit: any) => {
            if (hit.name) {
              const cityLabel = hit.country 
                ? `${hit.name}, ${hit.country}`
                : hit.name;
              
              locationOptions.push({
                label: cityLabel,
                value: hit.name,
                type: 'city',
                country: hit.country,
              });
            }
          });
        }
      }
      
      // Process country results
      if (type === 'country' || type === 'both') {
        const countryResults = type === 'both' ? results[1] : results[0];
        if (countryResults?.hits) {
          countryResults.hits.forEach((hit: any) => {
            if (hit.name) {
              locationOptions.push({
                label: hit.name,
                value: hit.name,
                type: 'country',
              });
            }
          });
        }
      }
      
      setOptions(locationOptions);
    } catch (error) {
      console.error('Error searching locations:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Load locations when input value changes
  useEffect(() => {
    // Only search when user is actually typing
    if (inputValue && inputValue.length >= 2) {
      // Debounce the search
      const timeoutId = setTimeout(() => {
        searchLocations(inputValue);
      }, 300);

      return () => clearTimeout(timeoutId);
    } else {
      // Clear options when input is too short
      setOptions([]);
    }
  }, [inputValue, type]);

  return (
    <StyledFormControl 
      fullWidth={fullWidth}
      disabled={disabled}
      required={required}
      error={error}
    >
      <Autocomplete
        id={selectId}
        options={options}
        value={selectedOption}
        onChange={(_, newValue) => {
          if (newValue) {
            onChange(newValue.value, newValue);
          } else {
            onChange('');
          }
        }}
        inputValue={selectedOption ? selectedOption.label : inputValue}
        onInputChange={(_, newInputValue, reason) => {
          // Only update inputValue for typing, clear the selection when typing
          if (reason === 'input') {
            setInputValue(newInputValue);
            // Clear the current selection to allow new search
            if (selectedOption) {
              onChange('');
            }
          } else if (reason === 'clear') {
            setInputValue('');
            onChange('');
          }
        }}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        loading={loading}
        loadingText="Searching locations..."
        noOptionsText={inputValue.length < 2 ? "Type at least 2 characters" : "No locations found"}
        size={size}
        // Keep the input showing the selected label when an option is selected
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {option.type === 'country' ? (
                <PublicIcon sx={{ mr: 1.5, color: 'text.secondary', fontSize: 20 }} />
              ) : (
                <LocationOnIcon sx={{ mr: 1.5, color: 'text.secondary', fontSize: 20 }} />
              )}
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {option.label}
              </Typography>
            </Box>
          </Box>
        )}
        filterOptions={(x) => x} // Don't filter since we handle search ourselves
      />
      {helperText && (
        <FormHelperText error={error}>
          {helperText}
        </FormHelperText>
      )}
    </StyledFormControl>
  );
};

export default LocationSearchSelect;